import React from 'react';
import classNames from 'classnames';

import TreeTableToggle, { TreeTableToggleSpacer } from './TreeTableToggle';
import { FlatTreeRow, TreeTableColumn, TreeTableTreeColumn } from './types';

interface ComponentProps<T> {
  node: FlatTreeRow<T>;
  treeColumn: TreeTableTreeColumn<T>;
  columns: TreeTableColumn<T>[];
  onToggle: (id: string) => void;
  toggleLabel: (row: T, expanded: boolean, depth: number) => string;
  /** Extra class names after the generic `tree-table-row depth-{n}` ones. */
  className?: string;
  dataCy: string;
}

/** One `<tr>`: the row header cell (indent, toggle or spacer, tree column content) then the data cells. */
const TreeTableBodyRow = <T,>({
  node,
  treeColumn,
  columns,
  onToggle,
  toggleLabel,
  className,
  dataCy,
}: ComponentProps<T>): React.ReactElement => {
  const { row, depth, expanded, collapsible } = node;
  const { id, data } = row;

  return (
    <tr className={classNames('tree-table-row', `depth-${depth}`, className)} data-cy={`${dataCy}-row-${id}`}>
      <th scope="row" className="tree-table-tree-cell" style={{ '--tree-depth': depth } as React.CSSProperties}>
        <span className="tree-table-tree-cell-inner">
          {collapsible ? (
            <TreeTableToggle
              expanded={expanded}
              label={toggleLabel(data, expanded, depth)}
              onToggle={() => onToggle(id)}
              dataCy={`${dataCy}-toggle-button-${id}`}
            />
          ) : (
            <TreeTableToggleSpacer />
          )}
          <span className="tree-table-tree-cell-content">{treeColumn.render(data, depth)}</span>
        </span>
      </th>
      {columns.map(({ key, align, render }) => (
        <td key={key} className={classNames({ 'align-end': align === 'end' })}>
          {render(data, depth)}
        </td>
      ))}
    </tr>
  );
};

export default TreeTableBodyRow;
